import type {ThreadMessageLike} from '@assistant-ui/react'
import type {Message} from './hooks/useChatMessageFormatting'
import {omicsMessageToAssistantParts, sanitizeAssistantParts} from './omicsMessageToAssistantParts'

export interface AssistantUiIndexFingerprint {
  messageCount: number
  partCountByMessageId: ReadonlyMap<string, number>
}

export const EMPTY_ASSISTANT_UI_INDEX_FINGERPRINT: AssistantUiIndexFingerprint = {
  messageCount: 0,
  partCountByMessageId: new Map(),
}

/** Invisible part so assistant-ui `tapClientLookup` indexes never shrink. */
export const OMICS_ASSISTANT_INDEX_PAD_NAME = 'omics-index-pad'

export function isAssistantUiIndexError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const {message} = error
  return (
    (message.includes('tapClientLookup') || message.includes('useClientLookup')) &&
    message.includes('out of bounds')
  )
}

let lastAssistantUiIndexAutoResetAt = 0

/** Guard against a tight crash→reset loop; returns whether a reset should run now. */
export function shouldAutoResetAssistantUiIndexError(error: unknown, minIntervalMs = 400): boolean {
  if (!isAssistantUiIndexError(error)) return false
  const now = Date.now()
  if (now - lastAssistantUiIndexAutoResetAt < minIntervalMs) return false
  lastAssistantUiIndexAutoResetAt = now
  return true
}

function assistantIndexPadPart(slot: number): {
  type: 'data'
  name: typeof OMICS_ASSISTANT_INDEX_PAD_NAME
  data: {slot: number}
} {
  return {type: 'data', name: OMICS_ASSISTANT_INDEX_PAD_NAME, data: {slot}}
}

/**
 * Grow `content` to `highWater` with invisible data pads. Empty text/reasoning parts are
 * stripped by assistant-ui (`fromThreadMessageLike`), so pads must be `data` parts.
 */
export function padAssistantLikeContent(
  content: ThreadMessageLike['content'],
  highWater: number,
): Exclude<ThreadMessageLike['content'], string> {
  if (typeof content === 'string') {
    return padAssistantLikeContent([{type: 'text', text: content}], highWater)
  }
  if (content.length >= highWater) return content
  const padded = [...content]
  for (let slot = content.length; slot < highWater; slot += 1) {
    padded.push(assistantIndexPadPart(slot))
  }
  return padded
}

export function raiseAssistantUiPartHighWater(
  highWater: Map<string, number>,
  fingerprint: AssistantUiIndexFingerprint,
): void {
  for (const [id, count] of fingerprint.partCountByMessageId) {
    highWater.set(id, Math.max(highWater.get(id) ?? 0, count))
  }
  for (const id of [...highWater.keys()]) {
    if (!fingerprint.partCountByMessageId.has(id)) {
      highWater.delete(id)
    }
  }
}

/** Part count `convertMessage` feeds assistant-ui (`tapClientLookup` indexes these). */
export function assistantUiContentPartCount(msg: Message): number {
  if (msg.type === 'user') return 1
  return sanitizeAssistantParts(omicsMessageToAssistantParts(msg)).length
}

/**
 * Counting parts formats markdown for the whole thread, so counts are memoized per
 * `Message` **object**: rows are rebuilt immutably on every change (`{...row, …}`), so a
 * cache hit means nothing feeding the part list moved.
 *
 * Memoizing on a cheap "is this text empty" key instead drifts, and the drift is what
 * made `tapClientLookup` throw: such a key reads the **raw** text, while the part list
 * drops a part when the **formatted** text is empty — and `formatAgentXmlToMarkdown`
 * strips `<think>`, `<solution>`, `<tool/>` and empty `<observation>`, so raw-nonempty
 * text can render to nothing. A lead that started as `<think>` and then grew real prose
 * kept its key, so the memoized count stayed one below reality; the finalize that really
 * did drop a part then compared against that stale number, no remount was scheduled, and
 * the still-mounted part at the lost index blew up.
 */
const partCountByMessage = new WeakMap<Message, number>()

function memoizedPartCount(msg: Message): number {
  // A streaming row is the one place a shared timeline object is still being appended to,
  // so recount it every time rather than trusting object identity. It is also the row that
  // changes on every token, so the memo would miss anyway.
  if (msg.isStreamingTurn) return assistantUiContentPartCount(msg)
  const cached = partCountByMessage.get(msg)
  if (cached !== undefined) return cached
  const count = assistantUiContentPartCount(msg)
  partCountByMessage.set(msg, count)
  return count
}

export function captureAssistantUiIndexFingerprint(
  messages: readonly Message[],
): AssistantUiIndexFingerprint {
  const partCountByMessageId = new Map<string, number>()
  for (const msg of messages) {
    partCountByMessageId.set(msg.id, memoizedPartCount(msg))
  }
  return {messageCount: messages.length, partCountByMessageId}
}

/**
 * assistant-ui's `tapClientLookup` throws when a message or part index disappears
 * (`Index N out of bounds (length: M)`). Growing is safe; shrinking is not.
 */
export function didAssistantUiIndexSpaceShrink(
  prev: AssistantUiIndexFingerprint,
  next: AssistantUiIndexFingerprint,
): boolean {
  if (next.messageCount < prev.messageCount) return true
  for (const [id, prevCount] of prev.partCountByMessageId) {
    const nextCount = next.partCountByMessageId.get(id)
    if (nextCount === undefined || nextCount < prevCount) return true
  }
  return false
}
